import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr, localizedStatic } from "@/i18n/copy";
import NotificationInbox from "@/components/NotificationInbox";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import PushDeviceSettings from "@/components/PushDeviceSettings";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, CheckCheck, ChevronDown, RefreshCw, Settings2 } from "lucide-react";
import { safeNotificationRoute } from "@/pwa";

const DEFAULTS = {
  in_app: true, appointments: true, appointment_changes: true, payments: true,
  quotations: true, new_customers: true, projects: true, loyalty: true,
  marketing: false, team: true, email_enabled: false,
};

const LABELS = localizedStatic({
  in_app: ["Notifiche interne", "Mostra gli avvisi direttamente nel gestionale"],
  appointments: ["Nuovi appuntamenti", "Avvisa quando viene creato un appuntamento"],
  appointment_changes: ["Modifiche appuntamenti", "Cambi, spostamenti e cancellazioni"],
  payments: ["Pagamenti", "Incassi, acconti e pagamenti registrati"],
  quotations: ["Preventivi", "Nuovi preventivi e preventivi accettati"],
  new_customers: ["Nuovi clienti", "Nuove anagrafiche e richieste"],
  projects: ["Progetti tattoo", "Aggiornamenti sui progetti e sul loro stato"],
  loyalty: ["Loyalty & Bonus", "Riscatti, punti e gift card"],
  marketing: ["Marketing", "Campagne e attività promozionali"],
  team: ["Team", "Inviti, ruoli e modifiche ai membri"],
  email_enabled: ["Email", "Abilita l'invio email quando un provider sarà configurato"],
}, [["in_app",0],["in_app",1],["appointments",0],["appointments",1],["appointment_changes",0],["appointment_changes",1],["payments",0],["payments",1],["quotations",0],["quotations",1],["new_customers",0],["new_customers",1],["projects",0],["projects",1],["loyalty",0],["loyalty",1],["marketing",1],["team",0],["team",1],["email_enabled",0],["email_enabled",1]]);

export default function Notifications() {
  useUiLanguage();
  const nav = useNavigate();
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try { setItems((await api.get("/notifications")) || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/notification-preferences").then((p) => setPrefs({ ...DEFAULTS, ...p })).catch(() => {});
    loadItems();
    const refresh = () => { if (document.visibilityState === "visible") loadItems(); };
    window.addEventListener("tps:notifications-changed", refresh);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("tps:notifications-changed", refresh);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/notification-preferences", prefs); toast.success(tr("Preferenze notifiche salvate")); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const openNotification = async (item) => {
    try {
      if (!item.read) {
        await api.post(`/notifications/${item.id}/read`, {});
        setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, read: true } : entry));
        window.dispatchEvent(new Event("tps:notifications-changed"));
      }
      const route = safeNotificationRoute(item.link);
      if (route) nav(route);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all", {});
      setItems((current) => current.map((item) => ({ ...item, read: true })));
      window.dispatchEvent(new Event("tps:notifications-changed"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const unread = items.filter((item) => !item.read).length;

  return <div className="animate-fade-up">
    <PageHeader
      subtitle={tr("Apri gli aggiornamenti che richiedono la tua attenzione. Le preferenze restano disponibili senza occupare il flusso di lavoro.")}
      title={tr("Notifiche")}
      action={<Button variant="outline" onClick={loadItems}><RefreshCw className="mr-2 h-4 w-4"/>{tr("Aggiorna")}</Button>}
    />

    <PushDeviceSettings />

    <NotificationInbox />

    <details className="group mt-4 rounded-2xl border bg-card/40">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
        <span className="flex items-center gap-3"><Settings2 className="h-4 w-4 text-muted-foreground"/><span><b className="text-sm">{tr("Preferenze notifiche")}</b><small className="mt-0.5 block text-muted-foreground">{tr("Scegli quali categorie vuoi ricevere")}</small></span></span>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180"/>
      </summary>
      <div className="border-t p-5">
        {Object.entries(LABELS).map(([key, [title, desc]]) => <div key={key} className="flex items-center justify-between gap-4 border-b border-border py-3 last:border-0">
          <div><div className="text-sm font-medium">{title}</div><div className="mt-1 text-xs text-muted-foreground">{desc}</div></div>
          <Switch checked={!!prefs[key]} onCheckedChange={(value) => setPrefs({ ...prefs, [key]: value })}/>
        </div>)}
        <div className="mt-4 flex justify-end"><Button onClick={save} disabled={saving}>{saving ? tr("Salvataggio...") : tr("Salva preferenze")}</Button></div>
      </div>
    </details>
  </div>;
}


